import { inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { CanActivateChildFn, Router, UrlTree } from '@angular/router';
import { Observable, filter, take, switchMap, of } from 'rxjs';
import { MenuService } from '../services/menu.service';
import { SubscriptionStateService } from '../services/subscription-state.service';
import { resolveActive } from '../services/nav-tree.builder';
import { NavNode } from '../models/nav.model';
import { environment } from '../../../environments/environment';

/**
 * Route-level permission enforcement for everything under `/app`.
 *
 * Hiding a menu item is a UX affordance, not a control: before this guard, typing
 * `/app/admin/permissions` opened the page for any authenticated user, because
 * `MenuService.filterByPermissions` only ever filtered what was *rendered*.
 *
 * Registered ONCE as `canActivateChild` on the `/app` route, so it runs on every child
 * navigation with no per-route annotation. The alternative — the pre-existing
 * `permissionGuard`, which reads `data.featureId` — would need that id hand-maintained on
 * ~280 routes and silently allows access whenever the id is missing.
 *
 * It reuses the navigation tree that already drives the sidebar, so there is exactly one
 * definition of "may this user see this?" in the app.
 *
 * NOT a security boundary. The API authorises every request independently
 * (`[Authorize]`, role policies, and the tenant query filter). This stops a user
 * stumbling into a page whose data they cannot load; it does not protect the data.
 */
export const navPermissionGuard: CanActivateChildFn = (_route, state): Observable<boolean | UrlTree> => {
  const menu = inject(MenuService);
  const router = inject(Router);
  const subState = inject(SubscriptionStateService);

  // A deep link activates this guard before ShellComponent exists, so the tree may not be
  // loaded (or even requested) yet. Kick it off, then wait for the first settled state.
  if (!menu.ready() && !menu.loading()) menu.loadNav().subscribe();

  return toObservable(menu.ready).pipe(
    filter(ready => ready),
    take(1),
    switchMap(() => of(decide(menu, router, state.url, subState.isRestricted()))),
  );
};

/**
 * Route areas that are deliberately OUTSIDE the navigation system.
 *
 * These have no MFeature rows by design — they are per-account pages reachable from a
 * direct link or button, not from the module tree (most from the profile menu;
 * /app/tenant/billing from the "Upgrade Plan" action on the Subscription page). Everything
 * else under /app is menu-governed and is denied when the user has no module for it.
 *
 * This is not a business-route allowlist; adding a feature page here would be a mistake.
 */
const NON_MENU_AREAS = ['/app/security/', '/app/auth/', '/app/tenant/billing'] as const;

function decide(menu: MenuService, router: Router, url: string, restricted: boolean): boolean | UrlTree {
  const path = url.split('?')[0].split('#')[0];

  // Checked FIRST, before any tree matching — these pages are deliberately outside the
  // menu/permission system entirely, so nothing about their resolution should depend on
  // whether they happen to sit under an otherwise menu-governed module. Checking this only
  // as a last-resort fallback (its previous position) meant it was dead code for any path
  // that also happened to prefix-match a real module — e.g. /app/tenant/billing, nested
  // under "Tenant Hub", never reached this line at all.
  if (NON_MENU_AREAS.some(a => path.startsWith(a))) return true;

  // Subscription AND grace period over: every module is locked. The user may still log in and reach billing
  // (above), the profile and security pages, but a typed module URL goes to billing, where the renewal
  // message is shown. The API refuses the module data independently; this just avoids a doomed page.
  if (restricted) return router.parseUrl('/app/tenant/billing');

  const tree = menu.tree();

  // The guard only decides once the tree has loaded successfully (see `menu.ready`), so an
  // empty index here means the API genuinely returned no modules for this user — e.g. a
  // staff member whose role group grants nothing. They get the dashboard (where the sidebar
  // explains that no modules are assigned) and nothing else; sending them to
  // /app/dashboard from every other URL would otherwise loop, so it is the one exception.
  if (!tree.index.length) {
    return path === '/app/dashboard' ? true : router.parseUrl('/app/dashboard');
  }

  const active = resolveActive(url, tree.index);

  // An exact page node matched. Its presence in the tree means it survived the permission
  // filter, so the user is entitled to it.
  if (active.pageId != null) {
    const page = tree.byId.get(active.pageId);
    if (page?.perms.any) return true;
    return denied(router, menu, url);
  }

  // Only the module matched, by path prefix — so this URL is deeper than any node the user
  // holds. Two very different cases, separated by the menu-managed path set:
  //
  //   a) the path IS menu-managed but absent from this user's tree -> the permission filter
  //      removed it -> DENY.
  //   b) the path is not in the menu at all (e.g. a button-triggered page with no MFeature
  //      row, or one of ~97 real routes like master-data/config/*) -> there is no
  //      permission to check -> allow. MFeature/permission checks are for menu items and
  //      real pages, not for every URL segment under an accessible section.
  //
  // Without GET /api/menu/paths these are indistinguishable, because the API pre-filters the
  // tree and a denied node simply is not in the response.
  if (active.moduleId != null) {
    const mod = tree.byId.get(active.moduleId);
    // NOT mod?.perms.any — a purely organisational parent (e.g. "Tenant Hub") very often
    // has no MappingFeatureGroup row of its own and reads IsView=false directly, even
    // though it's genuinely reachable: the backend includes it as a structural wrapper
    // whenever ANY child is accessible (see MenuService.GetMenuForUserAsync's ancestor-
    // inclusion comment), and the frontend tree mirrors that. So the module's mere
    // presence in tree.byId — which only happens when it was included for exactly that
    // reason — is the correct "is this section reachable at all" signal. Gating on the
    // module's own perms.any instead denied every non-menu-managed sub-path under an
    // accessible-but-ungranted parent (e.g. /app/tenant/billing, reached only via the
    // "Upgrade Plan" button, nested under Tenant Hub, no MFeature row of its own).
    if (!mod) return denied(router, menu, url);

    const normalised = path.replace(/\/+$/, '').toLowerCase() || path.toLowerCase();
    const isDeeperThanModule = mod.path ? normalised !== mod.path.toLowerCase() : true;

    if (isDeeperThanModule && tree.menuManagedPaths.has(normalised)) {
      return denied(router, menu, url);   // case (a)
    }
    return true;                          // case (b)
  }

  // Nothing in the user's tree owns this URL, and it wasn't a NON_MENU_AREAS path either
  // (checked up front) — they hold no module for this area.
  return denied(router, menu, url);
}

function denied(router: Router, menu: MenuService, url: string): UrlTree {
  if (!environment.production) {
    // eslint-disable-next-line no-console
    console.warn('[nav-permission] blocked %s — user holds no permitted menu node for it', url);
  }
  return router.parseUrl(fallbackUrl(menu));
}

/** Somewhere the user is actually allowed to be. */
function fallbackUrl(menu: MenuService): string {
  const tree = menu.tree();

  const dashboard = [...tree.byId.values()].find(n => n.path === '/app/dashboard');
  if (dashboard?.perms.any) return '/app/dashboard';

  const firstLeaf = (nodes: readonly NavNode[]): string | null => {
    for (const n of nodes) {
      if (n.children.length) {
        const deep = firstLeaf(n.children);
        if (deep) return deep;
      } else if (n.path && n.perms.any) {
        return n.path;
      }
    }
    return null;
  };

  return firstLeaf(tree.live) ?? '/app/dashboard';
}
